import { CATEGORIES, todayStr } from "../../constants/categories";

const GREEN = "#10B981";
const GREEN_BG = "#10B98118";
const AMBER = "#F59E0B";
const PURPLE = "#7C3AED";

export default function CalendarStyle({
  histMap, history, completedCount, streak, completionRate,
  calMonth, calYear, monthDays, firstDayOfWeek, monthNames,
  prevMonth, nextMonth, isCurrentMonth,
  selectedDay, setSelectedDay,
}) {
  // Calculate current month completed count for streak visual
  const monthKey = `${calYear}-${String(calMonth + 1).padStart(2, "0")}`;
  const monthItems = history.filter(h => h.date >= `${monthKey}-01` && h.date <= `${monthKey}-${String(monthDays).padStart(2, "0")}`);
  const monthCompleted = monthItems.filter(h => h.completed).length;

  return (
    <>
      {/* Hero Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px", marginBottom: "20px" }}>
        {[
          { icon: "✅", value: completedCount, label: "Hoàn thành", color: GREEN, gradient: `linear-gradient(135deg, ${GREEN}12, ${GREEN}08)` },
          { icon: "🔥", value: streak.current_streak, label: "Streak", color: AMBER, gradient: `linear-gradient(135deg, ${AMBER}12, ${AMBER}08)` },
          { icon: "🏆", value: streak.longest_streak, label: "Kỷ lục", color: PURPLE, gradient: `linear-gradient(135deg, ${PURPLE}12, ${PURPLE}08)` },
        ].map(stat => (
          <div key={stat.label} style={{
            background: "var(--bg-card)", border: `1px solid var(--border-secondary)`,
            borderRadius: "16px", padding: "18px 12px", textAlign: "center",
            boxShadow: "var(--card-shadow)", position: "relative", overflow: "hidden",
          }}>
            <div style={{ position: "absolute", inset: 0, background: stat.gradient, pointerEvents: "none" }} />
            <div style={{ fontSize: "14px", marginBottom: "6px" }}>{stat.icon}</div>
            <div style={{ fontSize: "28px", fontWeight: "800", color: stat.color, lineHeight: 1 }}>{stat.value}</div>
            <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "6px", fontWeight: "500" }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Calendar */}
      <div style={{
        background: "var(--bg-card)", border: "1px solid var(--border-secondary)",
        borderRadius: "18px", padding: "22px", marginBottom: "20px",
        boxShadow: "var(--card-shadow)",
      }}>
        {/* Month navigation */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <button onClick={prevMonth} style={{
            background: "var(--bg-inset)", border: "none", borderRadius: "8px",
            width: "32px", height: "32px", cursor: "pointer", fontSize: "16px",
            color: "var(--text-tertiary)", display: "flex", alignItems: "center", justifyContent: "center",
          }}>‹</button>
          <div style={{ fontSize: "16px", fontWeight: "700", color: "var(--text-primary)" }}>
            {monthNames[calMonth]} {calYear}
          </div>
          <button onClick={nextMonth} disabled={isCurrentMonth} style={{
            background: isCurrentMonth ? "transparent" : "var(--bg-inset)",
            border: "none", borderRadius: "8px",
            width: "32px", height: "32px",
            cursor: isCurrentMonth ? "default" : "pointer", fontSize: "16px",
            color: isCurrentMonth ? "var(--border-primary)" : "var(--text-tertiary)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>›</button>
        </div>

        {/* Weekday headers */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px", textAlign: "center", marginBottom: "4px" }}>
          {["T2", "T3", "T4", "T5", "T6", "T7", "CN"].map(d => (
            <div key={d} style={{
              fontSize: "11px", color: "var(--text-muted)", padding: "6px 0",
              fontWeight: "600", letterSpacing: "0.5px",
            }}>{d}</div>
          ))}
        </div>

        {/* Day cells */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px", textAlign: "center" }}>
          {Array.from({ length: (firstDayOfWeek + 6) % 7 }).map((_, i) => <div key={`e${i}`} />)}
          {Array.from({ length: monthDays }).map((_, i) => {
            const day = i + 1;
            const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
            const items = histMap[dateStr] || [];
            const hasCompleted = items.some(c => c.completed);
            const hasData = items.length > 0;
            const isToday = dateStr === todayStr;
            const isFuture = dateStr > todayStr;
            const isSelected = Array.isArray(selectedDay) && selectedDay[0]?.date === dateStr;

            return (
              <div key={day} onClick={() => !isFuture && hasData && setSelectedDay(items)}
                style={{
                  position: "relative", aspectRatio: "1",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexDirection: "column",
                  borderRadius: "12px", cursor: hasData && !isFuture ? "pointer" : "default",
                  boxSizing: "border-box",
                  background: hasCompleted
                    ? (isSelected ? GREEN : GREEN_BG)
                    : isSelected ? "var(--bg-hover)" : isToday ? "var(--bg-hover)" : "transparent",
                  border: isToday
                    ? `2px solid ${GREEN}`
                    : "2px solid transparent",
                  transition: "all 0.2s ease",
                }}>
                <span style={{
                  fontSize: "14px",
                  fontWeight: isToday ? "800" : hasCompleted ? "700" : "500",
                  color: hasCompleted && isSelected
                    ? "#fff"
                    : hasCompleted ? GREEN
                    : isFuture ? "var(--border-primary)"
                    : isToday ? "var(--text-primary)"
                    : hasData ? "var(--text-tertiary)"
                    : "var(--text-dim)",
                  lineHeight: 1,
                }}>{day}</span>
                {hasCompleted && !isSelected && (
                  <div style={{
                    marginTop: "2px", fontSize: "8px", lineHeight: 1,
                  }}>✅</div>
                )}
                {hasCompleted && isSelected && (
                  <div style={{
                    marginTop: "2px", fontSize: "8px", lineHeight: 1,
                  }}>✓</div>
                )}
                {hasData && !hasCompleted && !isFuture && (
                  <div style={{
                    position: "absolute", bottom: "4px",
                    width: "4px", height: "4px", borderRadius: "50%",
                    background: "var(--text-muted)",
                  }} />
                )}
              </div>
            );
          })}
        </div>

        {/* Mini streak indicator below calendar */}
        {streak.current_streak > 0 && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
            marginTop: "16px", padding: "10px",
            background: GREEN_BG, borderRadius: "10px",
          }}>
            <span style={{ fontSize: "14px" }}>🔥</span>
            <span style={{ fontSize: "13px", fontWeight: "600", color: GREEN }}>
              {streak.current_streak} ngày liên tiếp!
            </span>
            {streak.current_streak >= 7 && <span style={{ fontSize: "14px" }}>🎯</span>}
          </div>
        )}
      </div>

      {/* Month Summary */}
      {monthItems.length > 0 && (
        <div style={{
          background: "var(--bg-card)", border: "1px solid var(--border-secondary)",
          borderRadius: "18px", padding: "20px", marginBottom: "20px",
          boxShadow: "var(--card-shadow)",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
            <div style={{ fontSize: "13px", color: "var(--text-tertiary)", fontWeight: "600", letterSpacing: "1px" }}>
              Tổng kết tháng
            </div>
            <div style={{
              fontSize: "12px", color: GREEN, fontWeight: "700",
              background: GREEN_BG, padding: "4px 10px", borderRadius: "20px",
            }}>
              {monthCompleted}/{monthItems.length} hoàn thành
            </div>
          </div>

          {/* Progress bar */}
          <div style={{
            background: "var(--bg-inset)", borderRadius: "8px", height: "8px",
            overflow: "hidden", marginBottom: "16px",
          }}>
            <div style={{
              height: "100%", borderRadius: "8px",
              background: `linear-gradient(90deg, ${GREEN}, #34D399)`,
              width: `${monthItems.length > 0 ? (monthCompleted / monthItems.length) * 100 : 0}%`,
              transition: "width 0.5s ease",
            }} />
          </div>

          {/* Challenge list */}
          {monthItems.slice(0, 5).map((item, idx) => {
            const cat = CATEGORIES.find(c => c.id === item.category_id) || CATEGORIES[0];
            const dayNum = new Date(item.date + "T00:00:00").getDate();
            return (
              <div key={item.id} style={{
                display: "flex", alignItems: "center", gap: "10px", padding: "10px 0",
                borderBottom: idx < Math.min(monthItems.length, 5) - 1 ? "1px solid var(--border-secondary)" : "none",
              }}>
                <div style={{
                  width: "28px", height: "28px", borderRadius: "8px",
                  background: "var(--bg-inset)", display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "12px", fontWeight: "700", color: "var(--text-muted)",
                }}>{dayNum}</div>
                <span style={{ fontSize: "18px" }}>{cat.emoji}</span>
                <div style={{
                  flex: 1, fontSize: "13px", color: "var(--text-secondary)", fontWeight: "500",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>{item.title}</div>
                {item.completed ? (
                  <div style={{
                    width: "22px", height: "22px", borderRadius: "50%",
                    background: GREEN, display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <span style={{ color: "#fff", fontSize: "11px", fontWeight: "900" }}>✓</span>
                  </div>
                ) : (
                  <div style={{
                    width: "22px", height: "22px", borderRadius: "50%",
                    border: "2px solid var(--border-secondary)",
                  }} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
